import { Link } from "react-router";
import { Box, Button, Typography } from "@mui/material";
import { ErrorOutline as ErrorOutlineIcon } from "@mui/icons-material";
import PropTypes from "prop-types";

/**
 * Presentational "access denied" notice, shared by RouteErrorBoundary (403s during
 * route loading) and pages that fetch data manually and need to surface a 403 themselves.
 *
 * @param {number|null} status - HTTP status code, if known (used to pick a default title/message)
 * @param {string} [message] - Override the default message for the given status
 * @param {{ for: string, expired_on: string }|null} substitution - when the 403 was caused by a
 *   lapsed substitution, the backend-provided context to explain it to the user
 * @param {import('react').ReactNode} [actions] - Replaces the default "Seite neu laden" /
 *   "Zur Startseite" buttons, for callers whose denial does not go away on reload or at
 *   the app's home page (e.g. a portal the user's account has no access to at all)
 */
export default function AccessDeniedNotice({
  status = 403,
  message: message_override = null,
  substitution = null,
  actions = null,
}) {
  let title = "Kein Zugriff";
  let message = "Sie haben keine Berechtigung, diese Seite aufzurufen.";

  if (status === 404) {
    title = "Seite nicht gefunden";
    message = "Die angeforderte Seite existiert nicht.";
  } else if (status === 403) {
    if (substitution?.for) {
      const expired_on = substitution.expired_on
        ? new Date(substitution.expired_on).toLocaleDateString("de-DE")
        : null;
      message = expired_on
        ? `Ihre Stellvertretung (für ${substitution.for}) ist am ${expired_on} abgelaufen.`
        : `Ihre Stellvertretung (für ${substitution.for}) ist abgelaufen.`;
    }
  } else {
    title = "Ein unerwarteter Fehler ist aufgetreten";
    message =
      "Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.";
  }

  if (message_override) {
    message = message_override;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: 2,
        textAlign: "center",
        p: 4,
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main" }} />
      {status && (
        <Typography variant="h3" component="p" color="text.secondary">
          {status}
        </Typography>
      )}
      <Typography variant="h5" component="h1">
        {title}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: "40rem" }}
      >
        {message}
      </Typography>
      <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
        {actions ?? (
          <>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Seite neu laden
            </Button>
            <Button variant="contained" component={Link} to="/home">
              Zur Startseite
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

AccessDeniedNotice.propTypes = {
  status: PropTypes.number,
  message: PropTypes.string,
  substitution: PropTypes.shape({
    for: PropTypes.string,
    expired_on: PropTypes.string,
  }),
  actions: PropTypes.node,
};
